/* global print */

import React, { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import '../styles/Article.css';
import { Container } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { LoadingContext } from './context/LoadingContext';
import SEO from './SEO';
import usePrefetch from '../hooks/usePrefetch';
import getArticleDescription from '../helpers/getArticleDescription';
import ArticlePostComment from './ArticlePostComment';
import ArticleCommentList from './ArticleCommentList';
import { bool } from 'prop-types';
import NotFound from './NotFound';
import hasStoreName from '../helpers/hasStoreName';
import AppContext from './context/AppContext';
import ArticleForm from './ArticleForm';
import constants from '../constants.json' assert { type: 'json' };
import useDynamicPath from '../hooks/useDynamicPath';

// Article fetching func.
const fetchArticleData = async (handle = '') => {
    const response = await fetch(new URL(`/api/articles/handle/${handle}`, process.env.APP_URL));
    const data = await response.json();
    return data?.article ?? null;
};

export default function Article({ isMobile }) {
    const { appContext } = useContext(AppContext);
    // Loading state
    const {
        startLoading,
        finishLoading,
    } = useContext(LoadingContext);
    // Dynamic paths
    const articlePath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.ARTICLE_PATH });
    const articleBasePath = articlePath[0].pathBeforeParams;
    const { handle } = useParams();
    const {
        isLoaded: articleLoaded,
        data: articleData,
    } = usePrefetch({
        key: 'article',
        resourceKey: 'handle',
        resourceValue: handle,
        prefetchFn: async () => {
            await startLoading();
            const data = await fetchArticleData(handle);
            await finishLoading();
            return data;
        }
    });
    /** Add store name to title if not exists */
    const title = useMemo(() => {
        if (!articleLoaded
            || !articleData)
            return '';
        let title = articleData.seoTitle ?? articleData.title;
        if (!hasStoreName(title, appContext.config.storeName)) {
            title = `${title} - ${appContext.config.storeName}`;
        }
        return title;
    }, [articleLoaded, articleData]);
    if (articleLoaded === true && !articleData)
        return (<NotFound />);
    return (<Container
        style={{ padding: '0' }}
        fluid>
        {(articleLoaded === true
            && articleData.imageUrl) && (<figure
            className={'article--image'}
            style={{ backgroundImage: `url(${articleData.imageUrl})` }}></figure>)}
        <div
            className={'page--section'}>
            {articleLoaded === true && (<>
                {Object.keys(articleData).length > 0 && (<>
                    {articleData.seoHtml?.length > 0
                        ? <div
                            dangerouslySetInnerHTML={{ __html: articleData.seoHtml }}>
                        </div>
                        : <SEO
                            seoTitle={title}
                            seoDescription={articleData.seoDescription ?? articleData.ssrDescription ?? (articleData.bodyHtml ? getArticleDescription(articleData.bodyHtml) : '')}
                            canonicalUrl={new URL(`${articleBasePath}/${articleData.handle}`, process.env.APP_URL).toString()}
                            image={articleData.imageUrl}
                            seoType={'article'}
                            author={articleData.author}
                            createdAt={articleData.createdAt}
                            updatedAt={articleData.updatedAt}
                            pageContent={articleData.bodyHtml}
                            breadsArr={[
                                {
                                    title: 'Home',
                                    handle: '/',
                                },
                                {
                                    title: 'News',
                                    handle: `${articleBasePath}/`,
                                },
                                {
                                    title: articleData.title,
                                    handle: `${articleBasePath}/${articleData.handle}`,
                                },
                            ]}
                        />
                    }
                    <div
                        className={'article--heading'}>
                        <div
                            className={'article--meta'}>
                            <span
                                className={'article--meta-item'}>{DateTime.fromISO(articleData.publishedAt ?? articleData.createdAt).toLocaleString(DateTime.DATE_FULL)}</span>
                            <span
                                className={'article--meta-item'}>{articleData.author}</span>
                        </div>
                        <h1
                            className={'article--title'}>{articleData.title}</h1>
                        {articleData.Forms?.map((form) => (<ArticleForm
                            key={form.id}
                            form={form}
                            isMobile={isMobile}
                        />))}
                    </div>
                    <div
                        className={'article--body'}
                        data-article-id={articleData.id}>
                        <div
                            dangerouslySetInnerHTML={{ __html: articleData.bodyHtml }}></div>
                        {articleData.showPrintOption ? (
                            <button
                                onClick={() => print()}
                            >
                                Print this page
                            </button>
                        ) : null}
                        <ArticleCommentList
                            comments={articleData.Comments} />
                        <ArticlePostComment
                            articleId={articleData.id} />
                    </div>
                </>)}
            </>)}
        </div>
    </Container>);
}

Article.propTypes = {
    isMobile: bool,
};